import { createPortal } from "react-dom";
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import EditorTemplateGallery from "../../components/EditorTemplateGallery";
import VideoEditorWorkspace from "../../components/VideoEditorWorkspace";
import MRSSThumbnailCreator from "../../components/MRSSThumbnailCreator";
import api from "../../../../conf/api";
import { toast } from "react-toastify";
import { aspectOf, embeddedFrom, placeDocumentInBox, primaryVideo, removeEmbedded } from "@wooglobe/vre-editor";
import { asPreselected, buildRenderPayload, editSourceUrl, hasLocalMedia, loadStartTemplate, resumeDocumentFor } from "../../components/vreEditSession";
import TemplateSlotSwitch from "../../components/TemplateSlotSwitch";
import { pairFor } from "../hooks/templateSlots";
import useTabTemplate from "./useTabTemplate";
import useTabVideoSource from "./useTabVideoSource";
import VideoChoiceControl from "./VideoChoiceControl";
import ApplyToAllControl from "./ApplyToAllControl";
import CopyEditControl, { editVersion, isCopyOutdated, savedEditOf } from "./CopyEditControl";
import CategoryControl from "./CategoryControl";
import { canUseRawClips } from "./batchItems";

const NO_DOC = { key: null, doc: null };
const actionSx = { borderRadius: 1.5, textTransform: "none", fontWeight: 600, minHeight: 36 };

function Waiting({ text }) {
  return (
    <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5} role="status">
      <CircularProgress size={32} />
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );
}

/**
 * The active batch tab: its editor, or the template gallery when the tab has
 * no template yet. Mounted per tab (keyed by the tab), so leaving a tab drops
 * its editor and video; only the card's saved payload stays behind.
 *
 * The ref's `getPayload()` hands the dialog what to save:
 * `{payload}`, or `{error: "not-ready" | "local-media", message}`.
 */
const BatchEditorPane = forwardRef(function BatchEditorPane({
  item,
  services,
  userKey,
  caches,
  getTemplate,
  getTemplateChoices,
  templatesLoading,
  chosenTemplate,
  onChooseTemplate,
  onDirty,
  onCoverChange,
  askConfirm,
  dirty,
  active,
  siblings,
  onVideoChoice,
  slots,
  applyToAll,
  onCategoryChange,
  onExtraChange,
  onThumbnailChange,
}, ref) {
  const vertical = asPreselected(getTemplate(item.brandName, item.platform, "vertical"));
  const horizontal = asPreselected(getTemplate(item.brandName, item.platform, "horizontal"));
  const tpl = useTabTemplate({ item, chosen: chosenTemplate, vertical, horizontal, templatesLoading, fetchTemplate: caches.fetchTemplate });
  const source = useTabVideoSource({ item, rawVideos: caches.rawVideos });

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const docRef = useRef(NO_DOC);
  const [panelOpen, setPanelOpen] = useState(false);
  // Bumped to reopen the editor on a document changed from outside it (Copy edit).
  const [editorRun, setEditorRun] = useState(0);

  // Capture Thumbnail — same editor-snapshot flow as the single-video editor.
  const [editorReady, setEditorReady] = useState(false);
  const timeRef = useRef(0);
  const [thumbnailCreatorOpen, setThumbnailCreatorOpen] = useState(false);
  const [thumbnailCapturing, setThumbnailCapturing] = useState(false);
  const [thumbnailCapturedFrame, setThumbnailCapturedFrame] = useState(null);

  // The saved edit to resume, read once per template: saving this tab updates
  // the card, and that must not hand the open editor a "new" initial document.
  const payloadRef = useRef(item.config?.renderPayload);
  payloadRef.current = item.config?.renderPayload;
  const template = tpl.template;
  const resumeDocument = useMemo(() => resumeDocumentFor(payloadRef.current, template), [template]);

  useImperativeHandle(
    ref,
    () => ({
      getPayload() {
        const doc = docRef.current.doc;
        if (!template || !doc) {
          return { error: "not-ready", message: "Nothing to save yet — wait for the editor to load, or choose a template." };
        }
        if (hasLocalMedia(doc)) {
          return {
            error: "local-media",
            message: "Some media only exists in this browser (a blob: URL). Remove it or upload it through the editor, then save again.",
          };
        }
        return { payload: buildRenderPayload(template, doc) };
      },
    }),
    [template],
  );

  // The brand default (or its absence) only decides the template a tab opens
  // with; the user can always switch it from here at publish time.
  const showGallery = tpl.status === "gallery" || galleryOpen;

  /** Open `next` on this tab, asking first when edits made on another template would be lost. */
  const chooseTemplate = (next) => {
    if (!next) return;
    const same = template && String(template.id) === String(next.id) && template.version === next.version;
    const apply = () => {
      if (!same) docRef.current = NO_DOC;
      setGalleryOpen(false);
      if (!same) onChooseTemplate(next);
    };
    if (!same && dirty) {
      askConfirm("Start over with this template?", "Your edits on this tab were made on a different template and will be lost.", "Use this template", apply);
    } else {
      apply();
    }
  };
  const usePicked = () => chooseTemplate(picked);

  // YouTube's second template (2026-10-08): a Main / Second switch when the
  // brand has both; nothing at all otherwise.
  const slotPair = pairFor(getTemplateChoices?.(item.brandName, item.platform), template?.id);
  const [slotBusy, setSlotBusy] = useState(false);
  const pickSlot = (row) => {
    if (!row || String(row.template_id) === String(template?.id ?? "")) return;
    setSlotBusy(true);
    loadStartTemplate(asPreselected(row), caches.fetchTemplate)
      .then(({ template: loaded, notice }) => {
        if (loaded) chooseTemplate(loaded);
        else toast.error(notice || "Could not open that template.");
      })
      .finally(() => setSlotBusy(false));
  };

  const openGallery = () => {
    setPicked(template);
    setGalleryOpen(true);
  };

  // Copy edit: the editor holds its document, so the changed one goes into
  // docRef (which the workspace resumes from) and the editor reopens on it.
  const reopenWith = (doc) => {
    docRef.current = { ...docRef.current, doc };
    setEditorRun((n) => n + 1);
    onDirty();
  };

  const copyEditFrom = (platform) => {
    const host = docRef.current.doc;
    const guest = savedEditOf(siblings.find((s) => s.platform === platform)?.config);
    if (!host || !guest) {
      toast.error(`Could not read ${platform}'s saved edit — save that tab and try again.`);
      return;
    }
    // The video's real shape, when this editor has already loaded it (the same
    // WGID's video usually is), so layers land on the same spot of the picture.
    const videoAspect = aspectOf(primaryVideo(guest)?.source);
    const merged = placeDocumentInBox(host, guest, { label: platform, version: editVersion(guest), videoAspect });
    if (!merged) {
      toast.error(`This ${item.platform} template has no video box to put ${platform}'s edit in.`);
      return;
    }
    reopenWith(merged);
    toast.success(`${platform}'s edit is now inside the ${item.platform} template — save to keep it.`);
  };

  const handleCaptureThumbnail = async () => {
    const doc = docRef.current.doc;
    if (!editorReady || !doc || thumbnailCapturing) {
      toast.error("The editor is still loading — try again in a moment.");
      return;
    }
    setThumbnailCapturing(true);
    try {
      const response = await api.post(
        "videos/editor-snapshot/",
        { document: doc, time: timeRef.current },
        { timeout: 100000 },
      );
      setThumbnailCapturedFrame(response.data.data_uri);
      setThumbnailCreatorOpen(true);
    } catch (err) {
      toast.error("Failed to capture frame — please try again.");
    } finally {
      setThumbnailCapturing(false);
    }
  };

  const removeCopiedEdit = () => {
    const doc = docRef.current.doc;
    if (!doc) return;
    reopenWith(removeEmbedded(doc));
    toast.info(`The ${item.platform} template's own video is back — save to keep it.`);
  };

  const shownDoc = docRef.current.doc || resumeDocument || { elements: [] };
  const copiedFrom = embeddedFrom(shownDoc);
  // The copy is a snapshot: say so when the platform it came from has saved a
  // different edit since. Copies made before versions were stored cannot tell.
  const copyOutdated = isCopyOutdated(shownDoc, siblings);

  if (!showGallery && (tpl.status === "loading" || source.status === "loading")) {
    return <Waiting text={tpl.status === "loading" ? "Loading the brand template…" : "Reading the selected raw clips…"} />;
  }

  if (showGallery) {
    return (
      <Box flex={1} minHeight={0} display="flex" flexDirection="column">
        <Box flex={1} minHeight={0} sx={{ overflowY: "auto", p: { xs: 1, md: 2 } }}>
          {!tpl.notice && !galleryOpen && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {item.brandName} has no {item.platform} template assigned. Choose one for this tab.
            </Alert>
          )}
          <EditorTemplateGallery selectedId={picked?.id ?? null} onSelect={setPicked} notice={tpl.notice} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", pt: 1.5, flexShrink: 0 }}>
          {galleryOpen && (
            <Button variant="outlined" onClick={() => setGalleryOpen(false)} sx={{ ...actionSx, borderColor: "#e2e8f0", color: "text.secondary" }}>
              Keep current template
            </Button>
          )}
          <Button
            variant="contained"
            disabled={!picked}
            onClick={usePicked}
            sx={{ ...actionSx, fontWeight: 700, px: 3, background: picked ? "linear-gradient(135deg, #312e81, #4338ca)" : undefined }}
          >
            Use this template
          </Button>
        </Box>
      </Box>
    );
  }

  if (source.status === "error") {
    return (
      <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} p={3}>
        <Alert severity="error" sx={{ borderRadius: 2, maxWidth: 640 }}>
          Could not read one of the raw clips picked for this tab. Pick other clips or the final video below, or try the other video source.
        </Alert>
        <VideoChoiceControl
          item={item}
          siblings={siblings}
          finalAvailable={Boolean(editSourceUrl(item.video))}
          rawVideos={caches.rawVideos}
          onApply={onVideoChoice}
          onPanelChange={setPanelOpen}
        />
      </Box>
    );
  }

  // The template name and raw-clip count are not shown in the header (user
  // request 2026-10-06); the Raw clips button already carries the count.
  const leading = null;
  const trailing = (
    <>
      {onCategoryChange && <CategoryControl item={item} onChange={onCategoryChange} onExtraChange={onExtraChange} />}
      {source.notice && (
        <Tooltip title={source.notice}>
          <WarningAmberIcon aria-label={source.notice} sx={{ fontSize: 18, color: "warning.dark" }} />
        </Tooltip>
      )}
      {canUseRawClips(item.video) && (
        <VideoChoiceControl
          item={item}
          siblings={siblings}
          finalAvailable={Boolean(editSourceUrl(item.video))}
          rawVideos={caches.rawVideos}
          onApply={onVideoChoice}
          onPanelChange={setPanelOpen}
        />
      )}
      <CopyEditControl item={item} siblings={siblings} copiedFrom={copiedFrom} outdated={copyOutdated} onCopy={copyEditFrom} onRemove={removeCopiedEdit} onPanelChange={setPanelOpen} />
      {slotPair && <TemplateSlotSwitch pair={slotPair} currentId={template?.id} onPick={pickSlot} busy={slotBusy} />}
      {applyToAll && <ApplyToAllControl item={item} {...applyToAll} onPanelChange={setPanelOpen} />}
      {onThumbnailChange && (
        <Button
          size="small"
          variant="outlined"
          startIcon={thumbnailCapturing ? <CircularProgress size={14} color="inherit" /> : <PhotoCameraIcon />}
          onClick={handleCaptureThumbnail}
          disabled={!editorReady || thumbnailCapturing}
          sx={{ ...actionSx, minHeight: 26, height: 26, py: 0, px: 1, fontSize: 11.5, borderColor: alpha("#4338ca", 0.4), color: "#4338ca" }}
        >
          {thumbnailCapturing ? "Capturing…" : "Capture Thumbnail"}
        </Button>
      )}
      <Button size="small" variant="outlined" startIcon={<SwapHorizIcon />} onClick={openGallery} sx={{ ...actionSx, minHeight: 26, height: 26, py: 0, px: 1, fontSize: 11.5, borderColor: alpha("#4338ca", 0.4), color: "#4338ca" }}>
        Change template
      </Button>
    </>
  );

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column" gap={1}>
      {slots?.leading && slots?.trailing ? (
        <>
          {createPortal(leading, slots.leading)}
          {createPortal(trailing, slots.trailing)}
        </>
      ) : (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
          {leading}
          <Box flex={1} />
          {trailing}
        </Box>
      )}
      <VideoEditorWorkspace
        key={editorRun}
        template={template}
        videoUrl={source.videoUrl}
        videoSources={source.videoSources}
        video={item.video}
        services={services}
        userKey={userKey}
        docRef={docRef}
        onDirty={onDirty}
        onReadyChange={setEditorReady}
        active={active && !panelOpen}
        coverTimestamp={item.platform === "TIKTOK" ? item.config?.extra_config?.cover_timestamp : undefined}
        onCoverTimestampSave={item.platform === "TIKTOK" ? onCoverChange : undefined}
        resumeDocument={resumeDocument}
        timeRef={onThumbnailChange ? timeRef : undefined}
      />
      {onThumbnailChange && (
        <MRSSThumbnailCreator
          open={thumbnailCreatorOpen}
          setOpen={setThumbnailCreatorOpen}
          url={source.videoUrl}
          capturedFrame={thumbnailCapturedFrame}
          onSave={onThumbnailChange}
        />
      )}
    </Box>
  );
});

export default BatchEditorPane;
